import React from 'react';
import { NavLink } from 'react-router-dom';

import AlertIcon from '@hero/ui-kit/icons/utility/AlertIcon';
import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import CheckIcon from '@hero/ui-kit/icons/utility/CheckIcon';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import Container from '@hero/ui-kit/layout/Container';
import * as Style from './style.module.css';
import P from '@hero/ui-kit/typography/P';

export type DetailItem = {
    label: string;
    value: string;
    url?: string;
    returned?: boolean;
};

export type DeviceStatusLineItem = {
    title: string;
    details?: DetailItem[][];
    timestamp: string;
    alert?: boolean;
};

const DetailLineItem: React.FC<{ detail: DetailItem }> = ({ detail }) => {
    return (
        <div>
            <P noDefaultMargin strong>
                {detail.label}:{' '}
            </P>
            {detail.url ? (
                <NavLink to={detail.url} target="_blank" className="a-underline">
                    {detail.value}
                </NavLink>
            ) : (
                <span>{detail.value}</span>
            )}
        </div>
    );
};

const ReplacementDetails: React.FC<{
    replacementDetails: DetailItem[];
    returnDetails: DetailItem[];
    isReturned: boolean;
}> = ({ replacementDetails, returnDetails, isReturned }) => {
    const hasReplacementDetails = replacementDetails.length > 0;

    return (
        <Container
            gridTemplateColumns={hasReplacementDetails ? '1fr 1fr' : '1fr'}
            gridColumnGap="small"
            className={Style.replacementDetails}
        >
            {hasReplacementDetails && (
                <div>
                    <div className="label">Replacement device</div>
                    {replacementDetails.map((detail) => (
                        <DetailLineItem key={detail.label} detail={detail} />
                    ))}
                </div>
            )}

            <div>
                <div className="label">
                    <span>Return device</span>
                    <span
                        className={`${Style.returnedStatus} ${
                            !isReturned ? Style.notReturned : ''
                        }`}
                    >
                        {isReturned ? (
                            <>
                                <CheckIcon size="small" />
                                RETURNED
                            </>
                        ) : (
                            <>
                                <AlertIcon size="small" />
                                NOT RETURNED
                            </>
                        )}
                    </span>
                </div>
                {returnDetails.map((detail) => (
                    <DetailLineItem key={detail.label} detail={detail} />
                ))}
            </div>
        </Container>
    );
};

const StandardDetails: React.FC<{ details: DetailItem[][] }> = ({ details }) => {
    return (
        <>
            {details.map((detailColumn, detailColumnIndex) => (
                <React.Fragment key={detailColumnIndex}>
                    {detailColumn.map((detailLine) => (
                        <DetailLineItem key={detailLine.label} detail={detailLine} />
                    ))}
                </React.Fragment>
            ))}
        </>
    );
};

const DeviceLogLineItem: React.FC<{ item: DeviceStatusLineItem }> = ({ item }) => {
    const [expanded, setExpanded] = React.useState(false);

    const hasDetails = Boolean(item.details?.length);
    const hasReturnDevice = item.title === 'Replacement ordered' || item.title === 'Cancelled';

    const headerElement = (
        <Container
            gridTemplateColumns={'2.4rem 3fr 1fr'}
            gridColumnGap="small"
            className={Style.logLineItem}
        >
            <div className={Style.caretIcon}>
                {hasDetails && <CaretIcon size="small" direction={expanded ? 'down' : 'forward'} />}
            </div>

            <div className={`icon-title ${item.alert ? Style.alert : ''}`}>
                <P noDefaultMargin strong>
                    {item.title}
                </P>
                {item.alert && <AlertIcon size="small" />}
            </div>

            {item.timestamp && <div className="align-right">{item.timestamp}</div>}
        </Container>
    );

    const renderDetails = () => {
        if (!hasDetails || !expanded || !item.details) return null;

        if (hasReturnDevice) {
            const [replacementDetails = [], returnDetails = []] = item.details;
            return (
                <ReplacementDetails
                    replacementDetails={replacementDetails}
                    returnDetails={returnDetails}
                    isReturned={!item.alert}
                />
            );
        }

        return <StandardDetails details={item.details} />;
    };

    const detailsElement = renderDetails() ? (
        <div className={Style.lineItemDetails}>{renderDetails()}</div>
    ) : null;

    if (hasDetails) {
        return (
            <div>
                <ClickableOpacity
                    alt="Toggle details"
                    onClick={() => setExpanded(!expanded)}
                    styles={{ width: '100%', textAlign: 'left', lineHeight: 'inherit' }}
                >
                    {headerElement}
                </ClickableOpacity>
                {detailsElement}
            </div>
        );
    }

    return headerElement;
};

export default DeviceLogLineItem;
